import {
    useState,
    Fragment,
    useRef,
    type FC
} from "react";
import {
    TextInput as NativeTextInput,
    Pressable,
    View
} from "react-native";
import type IColorFieldProps from "./type";
import type {
    ColorFieldKeyPressEventType
} from "./type";
import stylesheet, {
    useStyles
} from "./stylesheet";
import {
    getIsColorFieldTextUnsettled,
    getColorFieldAlphaFromText,
    getColorFieldAlphaPercent,
    COLOR_FIELD_EMPTY_COLOR,
    getColorFieldAlphaHex,
    getColorFieldRecents,
    COLOR_FIELD_SESSION,
    getColorFieldValue
} from "./util";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../core/hooks";
import {
    useInteractionState
} from "../../helpers/interaction";
import {
    usePointerType
} from "../../helpers/pointer";
import {
    type IBottomSheetRef
} from "../bottomSheet/type";
import {
    type FieldInputRenderPropsType
} from "../fieldControl/type";
import {
    ChevronDown as ChevronDownIcon,
    ChevronUp as ChevronUpIcon
} from "lucide-react-native";
import {
    getFieldActionIconColors,
    getFieldSizeKey,
    getFieldColors,
    getFieldSize
} from "../fieldControl/stylesheet";
import {
    parseColorString,
    formatColorHex
} from "../../helpers/color";
import {
    COLOR_PICKER_DEFAULT_VALUE
} from "../colorPicker/util";
import {
    getSelectBoxControlElement
} from "../selectBox/util";
import {
    uuid
} from "../../utils";
import PopoverSurface from "../popoverSurface";
import AnimatedIcon from "../animatedIcon";
import FieldControl from "../fieldControl";
import BottomSheet from "../bottomSheet";
import ColorPicker from "../colorPicker";
import ColorSwatch from "../colorSwatch";
import Button from "../button";
import Text from "../text";

const ColorField: FC<IColorFieldProps> = ({
    onChangeEnd: onChangeEndProp,
    spreadBehaviour = "baseline",
    isEyeDropperEnabled = true,
    isAlphaEnabled = false,
    isContrastShown = true,
    isPickerEnabled = true,
    onChange: onChangeProp,
    isDisabled = false,
    isReadOnly = false,
    isRequired = false,
    isLoading = false,
    value: valueProp,
    contrastSurface,
    customLocalize,
    isShowSubTitle,
    initialValue,
    recentColors,
    customTheme,
    placeholder,
    isOptional,
    areaShape,
    hintText,
    subTitle,
    formats,
    presets,
    style,
    title,
    size,
    id,
    ...props
}) => {
    const {
        controlPaddings,
        controlHeights,
        shapeConfigs,
        touchTargets,
        typography,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const pointerType = usePointerType();

    const isCoarse = pointerType === "coarse";

    const inputRef = useRef<NativeTextInput>(null);

    const swatchRef = useRef<View>(null);

    const chevronRef = useRef<View>(null);

    const anchorRef = useRef<View>(null);

    const sheetRef = useRef<IBottomSheetRef>(null);

    const generatedID = useRef(uuid());

    const [
        innerValue,
        setInnerValue
    ] = useState(initialValue ?? "");

    const [
        text,
        setText
    ] = useState<string | null>(null);

    const [
        alphaText,
        setAlphaText
    ] = useState<string | null>(null);

    const [
        errorText,
        setErrorText
    ] = useState<string | undefined>(undefined);

    const [
        isFocused,
        setIsFocused
    ] = useState(false);

    const [
        isActive,
        setIsActive
    ] = useState(false);

    const [
        draft,
        setDraft
    ] = useState(COLOR_PICKER_DEFAULT_VALUE);

    const [
        sessionRecents,
        setSessionRecents
    ] = useState(COLOR_FIELD_SESSION.recents);

    const parsedValue = parseColorString(valueProp ?? innerValue);

    const hex = parsedValue ? formatColorHex({
        alpha: parsedValue.alpha,
        rgb: parsedValue.rgb,
        isAlphaEnabled
    }) : "";

    const alpha = parsedValue && isAlphaEnabled ? parsedValue.alpha : 1;

    const hasValue = hex !== "";

    const isInteractive = !isDisabled && !isReadOnly && !isLoading;

    const isPickerAvailable = isInteractive && isPickerEnabled;

    const inputID = id ?? generatedID.current;

    const currentType = errorText ? "danger" : "neutral";

    const currentSizeKey = getFieldSizeKey({
        isActionable: isPickerEnabled,
        pointerType,
        size
    });

    const currentSize = getFieldSize({
        size: currentSizeKey,
        controlPaddings,
        controlHeights,
        shapeConfigs,
        touchTargets,
        pointerType,
        typography,
        iconSizes,
        spaces
    });

    const currentColors = getFieldColors({
        type: currentType,
        isDisabled,
        isReadOnly,
        colors
    });

    const {
        isFocusVisible: isChevronFocusVisible,
        interactionHandlers: chevronHandlers,
        isHovered: isChevronHovered
    } = useInteractionState({
        isDisabled: !isPickerAvailable
    });

    const {
        alphaInput: alphaInputDynamicStyle,
        chevron: chevronDynamicStyle,
        input: inputDynamicStyle
    } = useStyles({
        isChevronFocusVisible,
        shapeConfigs,
        currentSize,
        pointerType,
        typography,
        radiuses,
        borders,
        colors,
        spaces
    });

    const chevronColors = getFieldActionIconColors({
        isHighlighted: isChevronHovered || isChevronFocusVisible,
        isDisabled: !isPickerAvailable,
        currentColors,
        colors
    });

    const pickerPresets = presets;

    const pickerRecents = recentColors ?? sessionRecents;

    const commitValue = (nextValue: string) => {
        if(valueProp === undefined) {
            setInnerValue(nextValue);
        }

        if(onChangeProp) {
            onChangeProp(nextValue);
        }
    };

    const commitEnd = (nextValue: string) => {
        if(onChangeEndProp) {
            onChangeEndProp(nextValue);
        }
    };

    const rememberValue = (nextValue: string) => {
        const nextRecents = getColorFieldRecents({
            recents: COLOR_FIELD_SESSION.recents,
            value: nextValue
        });

        COLOR_FIELD_SESSION.recents = nextRecents;

        setSessionRecents(nextRecents);
    };

    const handleOpen = () => {
        if(!isPickerAvailable) {
            return;
        }

        setErrorText(undefined);

        setText(null);

        anchorRef.current = getSelectBoxControlElement({
            node: swatchRef.current
        });

        if(isCoarse) {
            setDraft(hex === "" ? COLOR_PICKER_DEFAULT_VALUE : hex);
        }

        setIsActive(true);
    };

    const handleDismiss = () => {
        setIsActive(false);

        rememberValue(hex);
    };

    const handleToggle = () => {
        if(isActive) {
            handleDismiss();

            return;
        }

        handleOpen();
    };

    const handleSheetClosed = () => {
        setIsActive(false);
    };

    const handleApply = () => {
        commitValue(draft);

        commitEnd(draft);

        rememberValue(draft);

        sheetRef.current?.close();
    };

    const handlePickerChange = (nextValue: string) => {
        commitValue(nextValue);
    };

    const handlePickerChangeEnd = (nextValue: string) => {
        commitEnd(nextValue);
    };

    const handleDraftChange = (nextValue: string) => {
        setDraft(nextValue);
    };

    const handleChangeText = (nextText: string) => {
        setText(nextText);

        setErrorText(undefined);

        if(getIsColorFieldTextUnsettled(nextText)) {
            return;
        }

        const parsed = getColorFieldValue({
            isAlphaEnabled,
            text: nextText,
            alpha
        });

        if(parsed && parsed.hex !== hex) {
            commitValue(parsed.hex);
        }
    };

    const handleCommitText = () => {
        if(text === null) {
            return;
        }

        if(text.trim() === "") {
            setText(null);

            if(hasValue) {
                commitValue("");

                commitEnd("");
            }

            return;
        }

        const parsed = getColorFieldValue({
            isAlphaEnabled,
            alpha,
            text
        });

        if(!parsed) {
            setErrorText(localize("colorPicker-invalid", [
                hasValue ? hex : COLOR_PICKER_DEFAULT_VALUE
            ]));

            return;
        }

        setText(null);

        if(parsed.hex !== hex) {
            commitValue(parsed.hex);
        }

        commitEnd(parsed.hex);
    };

    const handleFocus = () => {
        setIsFocused(true);
    };

    const handleBlur = () => {
        setIsFocused(false);

        handleCommitText();
    };

    const handleKeyPress = (event: ColorFieldKeyPressEventType) => {
        if(event.nativeEvent.key === "Escape") {
            setText(null);

            setErrorText(undefined);
        }
    };

    const handleAlphaChangeText = (nextText: string) => {
        setAlphaText(nextText.replace(/\D/g, ""));
    };

    const handleAlphaCommit = () => {
        if(alphaText === null) {
            return;
        }

        setAlphaText(null);

        const percent = getColorFieldAlphaFromText(alphaText);

        if(percent === null) {
            return;
        }

        const nextHex = getColorFieldAlphaHex({
            percent,
            hex
        });

        if(nextHex !== hex) {
            commitValue(nextHex);
        }

        commitEnd(nextHex);
    };

    const getSubTitle = () => {
        if(isOptional) {
            return isRequired ? undefined : localize("is-optional");
        }

        if(!isShowSubTitle) {
            return undefined;
        }

        return subTitle ?? localize("is-optional");
    };

    const renderPrefix = (isShown: boolean) => {
        if(!isShown) {
            return null;
        }

        return <Text
            customColor={colors.content.text.low}
            customLocalize={customLocalize}
            customTheme={customTheme}
            style={stylesheet.affix}
            variant="monoMediumSize"
            aria-hidden={true}
        >
            {"#"}
        </Text>;
    };

    const renderAlpha = () => {
        if(!isAlphaEnabled || !hasValue) {
            return null;
        }

        return <View
            style={stylesheet.alphaGroup}
        >
            <Text
                customColor={colors.content.text.low}
                customLocalize={customLocalize}
                customTheme={customTheme}
                style={stylesheet.affix}
                variant="monoMediumSize"
                aria-hidden={true}
            >
                {"%"}
            </Text>
            <NativeTextInput
                value={alphaText ?? `${getColorFieldAlphaPercent(alpha)}`}
                placeholderTextColor={currentColors.placeholderColor}
                accessibilityLabel={localize("colorSlider-alpha")}
                aria-label={localize("colorSlider-alpha")}
                style={[
                    stylesheet.alphaInput,
                    alphaInputDynamicStyle,
                    {
                        color: currentColors.valueColor
                    }
                ]}
                onChangeText={handleAlphaChangeText}
                onSubmitEditing={handleAlphaCommit}
                underlineColorAndroid="transparent"
                onBlur={handleAlphaCommit}
                keyboardType="number-pad"
                editable={isInteractive}
                autoCorrect={false}
                multiline={false}
                maxLength={3}
            />
        </View>;
    };

    const renderChevron = () => {
        if(!isPickerEnabled || isLoading) {
            return null;
        }

        return <Pressable
            {...chevronHandlers}
            accessibilityLabel={localize("colorField-open")}
            aria-label={localize("colorField-open")}
            disabled={!isPickerAvailable}
            aria-expanded={isActive}
            style={[
                stylesheet.chevron,
                chevronDynamicStyle
            ]}
            aria-haspopup="dialog"
            onPress={handleToggle}
            ref={chevronRef}
            role="button"
        >
            <AnimatedIcon
                customColor={chevronColors.iconColor}
                size={currentSize.iconSize}
                fromIcon={ChevronDownIcon}
                isTransitioned={isActive}
                toIcon={ChevronUpIcon}
                iconTransition="morph"
                aria-hidden={true}
            />
        </Pressable>;
    };

    const renderInput = ({
        placeholderTextColor,
        accessibilityProps,
        style: inputStyle
    }: FieldInputRenderPropsType) => {
        return <Fragment>
            <Pressable
                onPress={isPickerAvailable ? handleToggle : undefined}
                importantForAccessibility="no-hide-descendants"
                style={stylesheet.swatch}
                aria-hidden={true}
                focusable={false}
                ref={swatchRef}
            >
                <ColorSwatch
                    color={hasValue ? hex : COLOR_FIELD_EMPTY_COLOR}
                    customLocalize={customLocalize}
                    customTheme={customTheme}
                    size="small"
                />
            </Pressable>
            {renderPrefix(hasValue || text !== null)}
            <NativeTextInput
                {...accessibilityProps}
                placeholder={placeholder ?? localize("colorField-placeholder")}
                value={text ?? (hasValue ? hex.slice(1, 7) : "")}
                placeholderTextColor={placeholderTextColor}
                underlineColorAndroid="transparent"
                onSubmitEditing={handleCommitText}
                onChangeText={handleChangeText}
                onKeyPress={handleKeyPress}
                editable={isInteractive}
                autoCapitalize="none"
                onFocus={handleFocus}
                style={[
                    stylesheet.input,
                    inputStyle,
                    inputDynamicStyle
                ]}
                onBlur={handleBlur}
                autoCorrect={false}
                spellCheck={false}
                multiline={false}
                ref={inputRef}
            />
            {renderAlpha()}
            {renderChevron()}
        </Fragment>;
    };

    const renderPicker = (isCompactPicker: boolean) => {
        return <ColorPicker
            onChange={isCompactPicker ? handleDraftChange : handlePickerChange}
            onChangeEnd={isCompactPicker ? undefined : handlePickerChangeEnd}
            isEyeDropperEnabled={isEyeDropperEnabled && !isCompactPicker}
            value={isCompactPicker ? draft : hasValue ? hex : undefined}
            spreadBehaviour={isCompactPicker ? "stretch" : "baseline"}
            isContrastShown={isContrastShown}
            contrastSurface={contrastSurface}
            customLocalize={customLocalize}
            isAlphaEnabled={isAlphaEnabled}
            isAreaFlush={!isCompactPicker}
            recentColors={pickerRecents}
            customTheme={customTheme}
            presets={pickerPresets}
            areaShape={areaShape}
            formats={formats}
        />;
    };

    const renderSheetBottom = () => {
        return <Button
            customTheme={customTheme}
            title={localize("apply")}
            spreadBehaviour="stretch"
            onPress={handleApply}
            variant="filled"
            type="primary"
        />;
    };

    const renderOverlay = () => {
        if(!isPickerEnabled) {
            return null;
        }

        if(isCoarse) {
            return <BottomSheet
                renderBottom={renderSheetBottom}
                customLocalize={customLocalize}
                key={`${inputID}-bottomsheet`}
                onClosed={handleSheetClosed}
                customTheme={customTheme}
                customKey={inputID}
                isAutoHeight={true}
                isShowHandle={true}
                isOpen={isActive}
                ref={sheetRef}
                title={title}
            >
                {renderPicker(true)}
            </BottomSheet>;
        }

        return <PopoverSurface
            width={shapeConfigs.drawerWidth + spaces.spacingMd}
            style={stylesheet.surface}
            returnFocusRef={inputRef}
            customTheme={customTheme}
            onDismiss={handleDismiss}
            triggerRef={chevronRef}
            anchorRef={anchorRef}
            isAutoFocused={true}
            isOpen={isActive}
            align="start"
            side="bottom"
        >
            {renderPicker(false)}
        </PopoverSurface>;
    };

    return <Fragment>
        <FieldControl
            {...props}
            onFocusRequest={isInteractive ? () => inputRef.current?.focus() : undefined}
            isFocused={isFocused || isActive}
            spreadBehaviour={spreadBehaviour}
            hintText={errorText ?? hintText}
            customLocalize={customLocalize}
            customTheme={customTheme}
            renderInput={renderInput}
            subTitle={getSubTitle()}
            isReadOnly={isReadOnly}
            isDisabled={isDisabled}
            isRequired={isRequired}
            isLoading={isLoading}
            size={currentSizeKey}
            type={currentType}
            inputID={inputID}
            title={title}
            style={style}
        />
        {renderOverlay()}
    </Fragment>;
};
export default ColorField;
